import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { askAiTokens as t } from "./tokens";
import { ToolEventEntry } from "./types";

interface Props {
  label: string;
  toolEvents?: ToolEventEntry[];
}

interface ToolItem {
  name: string;
  label: string;
  state: "active" | "done";
}

// Translate the structured tool events into a stable list keyed by tool
// name, so a "tool_call" followed by a later "tool_result" renders as ONE
// chip whose state and label flip in place.
const parseTools = (
  events?: ToolEventEntry[]
): { tools: ToolItem[]; uploads: string[] } => {
  const map = new Map<string, ToolItem>();
  const uploads: string[] = [];
  (events || []).forEach((e) => {
    if (e.kind === "tool") {
      map.set(e.name, { name: e.name, label: e.label, state: e.state });
    } else if (e.kind === "upload") {
      uploads.push(e.label);
    }
  });
  return { tools: Array.from(map.values()), uploads };
};

const ShimmerLabel: React.FC<{ text: string }> = ({ text }) => (
  <motion.span
    animate={{ backgroundPosition: ["-150% 0", "150% 0"] }}
    transition={{ duration: 2.2, ease: "linear", repeat: Infinity }}
    style={{
      display: "inline-block",
      fontSize: t.font.size.message,
      fontWeight: 500,
      letterSpacing: -0.1,
      backgroundImage:
        "linear-gradient(90deg, #475569 0%, #475569 30%, #60a5fa 50%, #475569 70%, #475569 100%)",
      backgroundSize: "200% 100%",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      color: "transparent",
    }}
  >
    {text}
  </motion.span>
);

const WaveDot: React.FC<{ delay: number }> = ({ delay }) => (
  <motion.span
    animate={{ y: [0, -3, 0], opacity: [0.35, 1, 0.35] }}
    transition={{
      duration: 1.1,
      ease: "easeInOut",
      repeat: Infinity,
      delay,
    }}
    style={{
      width: 5,
      height: 5,
      borderRadius: "50%",
      background: t.color.accent,
      display: "inline-block",
    }}
  />
);

// Live-indicator dot — solid colored dot with an outward ripple. Reads more
// like an "in progress" status than a generic spinner.
const PulseDot: React.FC<{ color: string }> = ({ color }) => (
  <span
    style={{
      position: "relative",
      width: 8,
      height: 8,
      display: "inline-flex",
      flexShrink: 0,
    }}
  >
    <motion.span
      aria-hidden
      animate={{ scale: [1, 2.4, 2.4], opacity: [0.55, 0, 0] }}
      transition={{
        duration: 1.6,
        ease: "easeOut",
        repeat: Infinity,
        times: [0, 0.7, 1],
      }}
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        background: color,
      }}
    />
    <span
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 0 1px ${color}33`,
      }}
    />
  </span>
);

const ToolChip: React.FC<{ tool: ToolItem; index: number }> = ({
  tool,
  index,
}) => {
  const isDone = tool.state === "done";
  const bg = isDone ? "#ecfdf5" : "#eff6ff";
  const border = isDone
    ? "rgba(22, 163, 74, 0.22)"
    : "rgba(37, 99, 235, 0.22)";
  const fg = isDone ? "#15803d" : "#2563eb";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        delay: index * 0.04,
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px 4px 9px",
        borderRadius: t.radius.pill,
        background: bg,
        border: `1px solid ${border}`,
        color: fg,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: t.font.sans,
        maxWidth: 360,
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isDone ? (
          <motion.span
            key="done"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 20 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 10,
              height: 10,
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            ✓
          </motion.span>
        ) : (
          <motion.span
            key="active"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            style={{ display: "inline-flex" }}
          >
            <PulseDot color={fg} />
          </motion.span>
        )}
      </AnimatePresence>
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          height: 16,
          minWidth: 80,
          fontFamily: t.font.sans,
          fontWeight: 500,
          textTransform: "none",
          letterSpacing: 0,
          overflow: "hidden",
        }}
        title={tool.name}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={tool.label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ whiteSpace: "nowrap" }}
          >
            {tool.label}
          </motion.span>
        </AnimatePresence>
      </span>
    </motion.div>
  );
};

const UploadChip: React.FC<{ label: string; index: number }> = ({
  label,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay: index * 0.04 }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: t.radius.pill,
      background: t.color.surfaceMuted,
      border: `1px solid ${t.color.border}`,
      color: t.color.textMuted,
      fontSize: 11,
    }}
  >
    {label}
  </motion.div>
);

const ThinkingIndicator: React.FC<Props> = ({ label, toolEvents }) => {
  const { tools, uploads } = useMemo(
    () => parseTools(toolEvents),
    [toolEvents]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: t.space.s3,
        paddingTop: t.space.s1,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: t.space.s2,
          // Lock height so the slide-in/out doesn't make sibling layout
          // jitter while the label changes.
          height: 22,
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 8, filter: "blur(2px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -8, filter: "blur(2px)" }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: "inline-block" }}
          >
            <ShimmerLabel text={label} />
          </motion.div>
        </AnimatePresence>
        <span style={{ display: "inline-flex", gap: 3 }}>
          <WaveDot delay={0} />
          <WaveDot delay={0.15} />
          <WaveDot delay={0.3} />
        </span>
      </div>
      {(tools.length > 0 || uploads.length > 0) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {uploads.map((u, i) => (
            <UploadChip key={`u-${i}`} label={u} index={i} />
          ))}
          <AnimatePresence>
            {tools.map((tool, i) => (
              <ToolChip key={tool.name} tool={tool} index={i} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ThinkingIndicator;
